import { useState, useRef, useEffect, useCallback } from 'react';
import { fetchGallery, fetchRemixData } from '@genfun/core/api/API';
import { LoginPopup } from '../popups/LoginPopup';
import { AccountPopup } from '../popups/AccountPopup';
import { SiteFooter } from '../components/SiteFooter';
import { downloadFile } from '../utils/download';
import './Gallery.css';

function HeaderMenu({ user, onNavigate, onSignOut }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="galHeaderMenu" ref={wrapRef}>
            <button
                className={`galHeaderMenuBtn${open ? ' galHeaderMenuBtn--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="More menu"
                aria-expanded={open}
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {open && (
                <div className="galHeaderMenuDropdown" role="menu">
                    <button className="galHeaderMenuItem galHeaderMenuItem--admin" role="menuitem" onClick={() => { setOpen(false); onNavigate('video'); }}>Generate video</button>
                    <button className="galHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('photo'); }}>Generate photo</button>
                    <button className="galHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('gallery'); }}>Gallery</button>
                    {user && (
                        <button
                            className="galHeaderMenuItem galHeaderMenuItem--danger"
                            role="menuitem"
                            onClick={() => { setOpen(false); onSignOut(); }}
                        >
                            Sign out
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function useLockBodyScroll(isOpen) {
    useEffect(() => {
        if (!isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        const prev = {
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            bodyPaddingRight: body.style.paddingRight,
        };
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            body.style.paddingRight = prev.bodyPaddingRight;
        };
    }, [isOpen]);
}

function GalleryTile({ item, onOpen }) {
    return (
        <button className="galItem" onClick={() => onOpen(item)}>
            {item.is_video ? (
                <video
                    className="galMedia"
                    src={item.url}
                    muted playsInline loop
                    poster={item.frame}
                    preload="metadata"
                    onMouseEnter={e => { e.target.play().catch(() => {}); }}
                    onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                />
            ) : (
                <img className="galMedia" src={item.url} alt="" loading="lazy" decoding="async" />
            )}
        </button>
    );
}

function Lightbox({ item, items, token, onClose, onChange }) {
    useLockBodyScroll(true);
    const idx = Math.max(0, items?.findIndex(i => (i.media_id || i.url) === (item.media_id || item.url)) ?? 0);
    const total = items?.length || 0;

    const [displayUrl, setDisplayUrl] = useState(item.url);
    const [fullUrl, setFullUrl] = useState(item.is_video ? item.url : null);

    useEffect(() => {
        setDisplayUrl(item.url);
        if (item.is_video) { setFullUrl(item.url); return; }
        if (!item.media_id || !token) { setFullUrl(item.url); return; }
        let cancelled = false;
        fetchRemixData(token, item.media_id).then(data => {
            const url = data?.url || item.url;
            if (cancelled) return;
            const preload = new Image();
            preload.onload = () => { if (!cancelled) setDisplayUrl(url); };
            preload.onerror = () => { if (!cancelled) setDisplayUrl(url); };
            preload.src = url;
            setFullUrl(url);
        }).catch(() => { if (!cancelled) setFullUrl(item.url); });
        return () => { cancelled = true; };
    }, [item.media_id, item.url, item.is_video, token]);

    const goto = useCallback((next) => {
        if (!items || items.length < 2) return;
        const ni = (next + items.length) % items.length;
        onChange?.(items[ni]);
    }, [items, onChange]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') goto(idx + 1);
            else if (e.key === 'ArrowLeft') goto(idx - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [idx, goto, onClose]);

    const created = item.created || item.created_at || null;
    const dateText = created
        ? new Date(created).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : 'Recently';

    return (
        <div className="galLightboxOverlay" onClick={onClose}>
            <button className="galLightboxClose" onClick={onClose} aria-label="Close">&times;</button>
            {total > 1 && (
                <button
                    className="galLightboxNav galLightboxNav--prev"
                    onClick={(e) => { e.stopPropagation(); goto(idx - 1); }}
                    aria-label="Previous"
                >‹</button>
            )}
            {total > 1 && (
                <button
                    className="galLightboxNav galLightboxNav--next"
                    onClick={(e) => { e.stopPropagation(); goto(idx + 1); }}
                    aria-label="Next"
                >›</button>
            )}
            <div className="galLightboxContent" onClick={e => e.stopPropagation()}>
                {item.is_video ? (
                    <video className="galLightboxMedia" src={item.url} autoPlay loop muted playsInline controls />
                ) : (
                    <img className="galLightboxMedia" src={displayUrl} alt="" />
                )}
                <div className="galLightboxFooter">
                    <div className="galLightboxMeta">
                        {total > 1 && <span className="galLightboxMetaPos">{idx + 1} / {total}</span>}
                        <span className="galLightboxMetaDot">·</span>
                        <span>{dateText}</span>
                    </div>
                    {fullUrl && (
                        <button type="button" className="galLightboxDownload" onClick={() => downloadFile(fullUrl)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            Download
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function Gallery({ token, user, setSignedIn, onNavigate }) {
    const [tab, setTab] = useState('video'); // 'video' | 'photo'
    const [videos, setVideos] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxItem, setLightboxItem] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const [showAccount, setShowAccount] = useState(false);

    useEffect(() => {
        if (!token) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        Promise.all([
            fetchGallery([], 0, true, token, false, false, true, 200).catch(() => null),
            fetchGallery([], 0, false, token, false, false, true, 200).catch(() => null),
        ]).then(([v, p]) => {
            if (cancelled) return;
            const vList = (v?.content && Array.isArray(v.content)) ? v.content : [];
            const pRaw = (p?.content && Array.isArray(p.content)) ? p.content : [];
            // The non-video feed can include videos — keep photos only.
            const pList = pRaw.filter(i => !i.is_video);
            setVideos(vList);
            setPhotos(pList);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [token]);

    const signOut = () => {
        localStorage.removeItem('igenfuntoken');
        window.location.reload();
    };

    const items = tab === 'video' ? videos : photos;

    return (
        <div className="galPage">
            {/* Header */}
            <div className="galHeader">
                <div className="galHeaderLeft">
                    <span className="galLogo" style={{ cursor: 'pointer' }} onClick={() => onNavigate('landing')}>D33P</span>
                    <nav className="galNav">
                        <button className="galNavBtn galNavBtn--admin" onClick={() => onNavigate('video')}>Generate video</button>
                        <button className="galNavBtn" onClick={() => onNavigate('photo')}>Generate photo</button>
                        <button className="galNavBtn galNavBtn--active">Gallery</button>
                    </nav>
                </div>
                <div className="galHeaderRight">
                    {!user ? (
                        <button className="galLoginBtn" onClick={() => setShowLogin(true)}>Log in</button>
                    ) : (
                        <>
                            <button className="galCredits" onClick={() => setShowAccount(true)}>{user.credits ?? 0} tokens</button>
                            <button className="galSignOutBtn galSignOutBtn--desktopOnly" onClick={signOut}>Sign out</button>
                        </>
                    )}
                    <HeaderMenu user={user} onNavigate={onNavigate} onSignOut={signOut} />
                </div>
            </div>

            <div className="galSection">
                <div className="galHead">
                    <span className="galEyebrow">[ ARCHIVE ]</span>
                    <h1 className="galTitle">Your generations</h1>
                </div>

                <div className="galTabs">
                    <button
                        className={`galTab${tab === 'video' ? ' galTab--active' : ''}`}
                        onClick={() => setTab('video')}
                    >
                        Videos <span className="galTabCount">{videos.length}</span>
                    </button>
                    <button
                        className={`galTab${tab === 'photo' ? ' galTab--active' : ''}`}
                        onClick={() => setTab('photo')}
                    >
                        Photos <span className="galTabCount">{photos.length}</span>
                    </button>
                </div>

                {!token ? (
                    <div className="galGate">
                        <span className="galGateText">Sign in to see your generations</span>
                        <button className="galGateBtn" onClick={() => setShowLogin(true)}>Log in</button>
                    </div>
                ) : loading ? (
                    <div className="galLoading">Loading your archive…</div>
                ) : items.length > 0 ? (
                    <div className="galGrid">
                        {items.map((item, idx) => (
                            <GalleryTile
                                key={item.media_id || item.url || idx}
                                item={item}
                                onOpen={setLightboxItem}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="galEmpty">
                        <span className="galEmptyIcon">{tab === 'video' ? '🎬' : '📸'}</span>
                        <span className="galEmptyText">No {tab === 'video' ? 'videos' : 'photos'} yet</span>
                        <button
                            className="galEmptyBtn"
                            onClick={() => onNavigate('photo')}
                        >
                            Create one
                        </button>
                    </div>
                )}
            </div>

            <LoginPopup isOpen={showLogin} setIsOpen={setShowLogin} setSignedIn={setSignedIn} />
            <AccountPopup isOpen={showAccount} onClose={() => setShowAccount(false)} token={token} user={user} />

            {lightboxItem && (
                <Lightbox
                    item={lightboxItem}
                    items={items}
                    token={token}
                    onClose={() => setLightboxItem(null)}
                    onChange={setLightboxItem}
                />
            )}

            <SiteFooter onNavigate={onNavigate} />
        </div>
    );
}
